import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import supabase from "../../supabase";
import { tokens } from "../../theme";
import { formatCurrencyManual } from "../../utils/formatters";
import { applyPendingChequesBalance, getPendingChecksTotal } from "../../utils/chequesCash";
import { Num } from "./index";
import { IconCheck, IconBilling, IconWallet } from "./icons";

/** Tinte por tipo de caja, para reconocerlas de un vistazo. */
const toneFor = (description = "") => {
  const name = description.toLowerCase();
  if (name.includes("efectivo")) return { color: "#3f6b1f", bg: "#eef4e4", icon: IconWallet };
  if (name.includes("banco")) return { color: "#14675e", bg: "#e4f2f0", icon: IconWallet };
  if (name.includes("cheque")) return { color: "#45499b", bg: "#eceef8", icon: IconCheck };
  return { color: "#8a5a12", bg: "#fbf1e2", icon: IconBilling };
};

/**
 * Totales de caja. Antes era una fila de textos subrayados; ahora cada caja es
 * una ficha con su saldo, y arriba el total consolidado.
 */
const CashTotals = ({ title = "Total en caja" }) => {
  const [cashes, setCashes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      const { data, error } = await supabase
        .from("cash")
        .select("*")
        .order("cashId", { ascending: true });

      if (error) {
        console.error("Error al obtener los totales de caja: ", error);
        if (active) setLoading(false);
        return;
      }

      /*
        El saldo guardado de la caja Cheques se desincroniza: hay flujos que
        cambian el estado de un cheque sin tocarlo, y otros que lo ajustan de
        forma incremental. Se recalcula desde los cheques pendientes al leer.
        Si ese cálculo falla, se muestra el saldo guardado antes que nada.
      */
      let cashesToShow = data || [];
      try {
        const pendingTotal = await getPendingChecksTotal();
        cashesToShow = applyPendingChequesBalance(cashesToShow, pendingTotal);
      } catch (pendingError) {
        console.error("Error al calcular el total de cheques pendientes: ", pendingError);
      }

      if (active) {
        setCashes(cashesToShow);
        setLoading(false);
      }
    };

    fetchData();
    return () => {
      active = false;
    };
  }, []);

  const total = cashes.reduce((acc, cash) => acc + (parseFloat(cash.balance) || 0), 0);

  return (
    <Box
      sx={{
        backgroundColor: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: `${tokens.radiusCard}px`,
        padding: { xs: "12px", md: "14px 16px" },
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        width: "100%",
        minWidth: 0,
      }}
    >
      {/*
        Grilla que se acomoda sola: entra en una línea cuando hay ancho y baja
        de renglón cuando no. Nunca obliga a scrollear de lado.
      */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(148px, 1fr))",
          gap: "8px",
          width: "100%",
          minWidth: 0,
        }}
      >
        {loading
          ? // Esqueletos: la altura no salta cuando llegan los saldos.
            Array.from({ length: 6 }).map((_, index) => (
              <Box
                key={`skeleton-${index}`}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "7px",
                  padding: "9px 11px",
                  border: `1px solid ${tokens.borderSoft}`,
                  borderRadius: "10px",
                  backgroundColor: tokens.surface2,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    height: 10,
                    width: "62%",
                    borderRadius: 999,
                    backgroundColor: tokens.borderSoft,
                    animation: "sup-pulse 1.2s ease-in-out infinite",
                  }}
                />
                <Box
                  sx={{
                    height: 16,
                    width: "84%",
                    borderRadius: 6,
                    backgroundColor: tokens.borderSoft,
                    animation: "sup-pulse 1.2s ease-in-out infinite",
                  }}
                />
              </Box>
            ))
          : cashes.map((cash) => {
          const tone = toneFor(cash.description);
          const Icon = tone.icon;
          const balance = parseFloat(cash.balance) || 0;

          return (
            <Box
              key={cash.cashId}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
                minWidth: 0,
                padding: "9px 11px",
                border: `1px solid ${tokens.borderSoft}`,
                borderRadius: "10px",
                backgroundColor: tokens.surface2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                <Box sx={{ display: "flex", flexShrink: 0, color: tone.color }}>
                  <Icon size={13} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: tokens.ink3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={cash.description}
                >
                  {cash.description}
                </Typography>
              </Box>

              <Num
                sx={{
                  fontSize: { xs: "13.5px", lg: "14.5px" },
                  fontWeight: 600,
                  lineHeight: 1.15,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: balance < 0 ? tokens.brandDark : tokens.ink,
                }}
                title={formatCurrencyManual(balance)}
              >
                {formatCurrencyManual(balance)}
              </Num>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "10px",
          borderTop: `1px solid ${tokens.border}`,
          paddingTop: "9px",
        }}
      >
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: tokens.ink3,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
        {loading ? (
          <Box
            sx={{
              height: 18,
              width: 132,
              borderRadius: 6,
              backgroundColor: tokens.borderSoft,
              animation: "sup-pulse 1.2s ease-in-out infinite",
            }}
          />
        ) : (
          <Num sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 600, whiteSpace: "nowrap" }}>
            {formatCurrencyManual(total)}
          </Num>
        )}
      </Box>
    </Box>
  );
};

export default CashTotals;
